import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container } from 'react-bootstrap';

import DealerUserSearch from './search/UsersSearch';
import UsersTable from './table/UsersTable';
import FilterButtons from '../common/components/FilterButtons';
import PageBreadcrumb from '../common/components/layout/PageBreadcrumb';

import { DealerUserStatusName } from '../constants/UserStatus';
import { UserRole } from '../constants/UserRole';
import RouterPaths from '../constants/RouterPaths';

import { setQueryString, useQueryString } from '../common/helpers/queryString';
import { PrepareUrl } from '../common/helpers/PrepareUrl';

import { searchUsers } from './UsersActions';
import { getDealer } from '../dealers/DealersActions';
import { AgreeOptionsCapitalized } from '../constants/AgreeOptions';

function UsersPage() {
  const dispatch = useDispatch();
  const { dealerId } = useParams();
  const query = useQueryString();
  const navigate = useNavigate();
  const location = useLocation();

  const previousSearchState = useSelector((state) => state.users.get('searchState'));
  const items = useSelector((state) => state.users.get('items'));
  const totalItems = useSelector((state) => state.users.get('totalItems'));
  const dealerDetails = useSelector((state) => state.dealers.get('dealerDetails'));

  const [searchParams, setSearchParams] = useState(
    previousSearchState?.searchParams || {
      offset: query.get('page') ? +query.get('page') - 1 : 0,
      organizationId: dealerId || null,
    },
  );

  const [dealerName, setDealerName] = useState(null);

  useEffect(() => {
    if (dealerId) {
      dispatch(getDealer(dealerId));
    } else {
      setDealerName(null);
    }
  }, [dispatch, dealerId]);

  useEffect(() => {
    if (dealerId && dealerDetails?.organization) {
      setDealerName(dealerDetails.organization.officialCompanyName || '');
    }
  }, [dealerDetails, dealerId]);

  useEffect(() => {
    setSearchParams((prev) => ({
      ...prev,
      organizationId: dealerId || null,
    }));
  }, [dealerId]);

  useEffect(() => {
    dispatch(searchUsers(searchParams));
  }, [dispatch, searchParams]);

  const handleChangeStatus = (status) => {
    setSearchParams((prev) => ({
      ...prev,
      status,
      offset: 0,
    }));
  };

  const handleSearch = (values, { setSubmitting }) => {
    const roles = {
      isBuyer: values?.role?.includes(UserRole.BUYER) || null,
      isSeller: values?.role?.includes(UserRole.SELLER) || null,
    };

    const getMarketingValue = (field) => {
      if (field?.value == null) return null;
      return field.value === AgreeOptionsCapitalized.YES;
    };

    const emailMarketingAllowed = getMarketingValue(values?.emailMarketingAllowed);
    const phoneMarketingAllowed = getMarketingValue(values?.phoneMarketingAllowed);
    const postMarketingAllowed = getMarketingValue(values?.postMarketingAllowed);
    const smsMarketingAllowed = getMarketingValue(values?.smsMarketingAllowed);

    setSearchParams((prev) => ({
      ...prev,
      ...values,
      ...roles,
      emailMarketingAllowed,
      phoneMarketingAllowed,
      postMarketingAllowed,
      smsMarketingAllowed,
      offset: 0,
    }));

    setSubmitting(false);
  };

  const changeOffset = (value) => {
    setSearchParams((prev) => ({
      ...prev,
      offset: value,
    }));

    navigate(setQueryString(location, 'page', value + 1));
  };

  const breadcrumbs = dealerId
    ? [
        { id: 1, type: 'home' },
        { id: 2, type: 'link', text: 'Dealers', url: RouterPaths.DEALERS },
        {
          id: 3,
          type: 'link',
          text: 'Edit Dealer',
          url: PrepareUrl(RouterPaths.DEALER_INFORMATION, { id: dealerId }),
        },
        { id: 4, type: 'span', text: 'Users' },
      ]
    : [
        { id: 1, type: 'home' },
        { id: 2, type: 'span', text: 'Users' },
      ];

  return (
    <Container fluid>
      <PageBreadcrumb items={breadcrumbs} pageTitle="Users" />
      <DealerUserSearch onSearch={handleSearch} dealerName={dealerName} />
      <FilterButtons
        data={DealerUserStatusName}
        onStatusChange={handleChangeStatus}
        previousSelectedStatus={previousSearchState?.searchParams?.status}
      />
      <UsersTable
        data={{ items, total: totalItems }}
        offset={searchParams.offset}
        changeOffset={changeOffset}
        searchParams={searchParams}
      />
    </Container>
  );
}

export default UsersPage;
